import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import {
  Button,
  FullTable,
  Modal,
  StepsIndicator,
  Title
} from '../../../../components';
import { AppContext } from '../../../../context/AppContext';
import { emptyRevisionProduct } from '../../../../emptyObjects';
import { useKeyPress, useNavigate, useResource } from '../../../../hooks';
import {
  RevisionProduct,
  Pagination,
  TableHeader
} from '../../../../interfaces';
import ProductForm from './components';

export const Step2 = () => {
  const {
    eraseResources,
    fetchResources
  } = useResource<RevisionProduct>();

  const navigate = useNavigate();
  const params = useParams();

  const { t } = useTranslation();

  const {
    apiType,
    setOpenModal,
    openModal,
    soundEffects
  } = useContext(AppContext);

  const [selectedRevisionProduct, setSelectedRevisionProduct] = useState<RevisionProduct>(
    emptyRevisionProduct
  );
  const [revisionProducts, setRevisionProducts] = useState<RevisionProduct[]>([]);
  const [currentComponent, setCurrentComponent] = useState<'product-list' | 'product-form'>('product-list');
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    perPage: 10,
    totalItems: 0
  });

  const headers: TableHeader[] = [
    {
      label: t('global.invoice'),
      value: 'invoice',
      format: 'none'
    },
    {
      label: t('global.status.title'),
      value: 'status',
      format: 'status'
    },
    {
      label: t('global.norm'),
      value: 'normCode',
      format: 'none'
    },
    {
      label: 'Fase',
      value: 'fase',
      format: 'none'
    },
    {
      label: t('global.brand'),
      value: 'brand',
      format: 'none'
    },
    {
      label: t('global.modelCode'),
      value: 'code',
      format: 'none'
    },
    {
      label: t('global.description'),
      value: 'description',
      format: 'none'
    },
    {
      label: t('global.presentation'),
      value: 'presentation',
      format: 'none'
    },
    {
      label: t('global.design'),
      value: 'indicators.design',
      format: 'boolean'
    },
    {
      label: t('global.urgent'),
      value: 'indicators.urgent',
      format: 'boolean'
    }
  ];

  const handleContinue = () => {
    if (revisionProducts.length <= 0) {
      Modal.fireError(t('services.errors.atLeastOneProduct'), setOpenModal);
      return false;
    }

    navigate(`/revision-request/step_3/${params.revisionServiceID}/${params.requestNumber}/${params.clientID}/${params.collaboratorID}`);
    return true;
  };

  const handleChangePage = (page: number) => {
    setPagination({
      ...pagination,
      currentPage: page
    });
  };

  const deleteRevisionProduct = (productID: string) => {
    Modal.fire(
      'warning',
      t('global.alert'),
      t('services.deleteProduct'),
      () => {
        eraseResources(
          `/${apiType}/revision-products`,
          {
            s_serviceID: params.revisionServiceID,
            s_productID: productID
          },
          () => {
            const auxRevisionproducts = revisionProducts.filter(
              (revisionProduct) => revisionProduct.id !== productID
            );

            setRevisionProducts(auxRevisionproducts);
            Modal.fireSuccess(t('global.correct'), t('services.deletedProduct'), setOpenModal, undefined, soundEffects);
          },
          (error: string) => Modal.fireError(error, setOpenModal, soundEffects)
        );
      },
      undefined,
      undefined,
      undefined,
      setOpenModal
    );
  };

  const deleteAllproducts = () => {
    Modal.fire(
      'warning',
      t('global.alert'),
      t('services.deleteProducts'),
      () => {
        eraseResources(
          `/${apiType}/revision-products`,
          {
            b_deleteAll: true,
            s_serviceID: params.revisionServiceID
          },
          () => {
            setRevisionProducts([]);
            Modal.fireSuccess(t('global.correct'), t('services.deletedProducts'), setOpenModal, undefined, soundEffects);
          },
          (error: string) => Modal.fireError(error, setOpenModal, soundEffects)
        );
      },
      undefined,
      undefined,
      undefined,
      setOpenModal
    );
  };

  useEffect(() => {
    if (currentComponent === 'product-list') {
      fetchResources(
        {
          resourcePath: `/${apiType}/revision-products`,
          filters: {
            s_serviceID: params.revisionServiceID || ''
          },
          pagination: {
            n_perPage: pagination.perPage,
            n_currentPage: pagination.currentPage,
            n_totalItems: pagination.totalItems
          }
        },
        (data) => {
          setRevisionProducts(data.items);
          setPagination(data.pagination);
        },
        (error: string) => Modal.fireError(error, setOpenModal, soundEffects)
      );
    }
  }, [currentComponent, pagination.currentPage, apiType]);

  useKeyPress(
    openModal ? () => { } : () => handleContinue(),
    [
      revisionProducts,
      currentComponent,
      openModal,
      selectedRevisionProduct
    ],
    currentComponent === 'product-list'
  );

  const component = {
    'product-list': (
      <div className='step-2__full-table-container'>
        <div className='step-2__action-buttons-container'>
          <Button
            onClick={() => {
              setSelectedRevisionProduct(emptyRevisionProduct);
              setCurrentComponent('product-form');
            }}
            type='secondary-outlined'
            label={t('services.addProducts') || ''}
            icon='plusRounded'
            iconPosition='top'
            transparent={true}
          />
          <Button
            onClick={() => deleteAllproducts()}
            type='secondary-outlined'
            label={t('services.deleteAllProducts') || ''}
            icon='minusRounded'
            iconPosition='top'
            transparent={true}
            disabled={pagination.totalItems === 0}
          />
        </div>
        <FullTable
          headers={headers}
          items={revisionProducts}
          currentPage={pagination.currentPage}
          perPage={pagination.perPage}
          totalItems={pagination.totalItems}
          onChangeCurrentPage={handleChangePage}
          onDeleteItem={(item: any) => deleteRevisionProduct(item.id)}
          onEditItem={(item: any) => {
            setSelectedRevisionProduct(item);
            setCurrentComponent('product-form');
          }}
        />
        <div className='step-2__button-container'>
          <Button
            onClick={() => navigate(`/revision-request/step_1/${params.revisionServiceID}`)}
            type='primary'
            label={t('global.goBack') || ''}
            icon='leftArrow'
            iconPosition='left'
          />
          <Button
            onClick={handleContinue}
            type='primary'
            label={t('global.continue') || ''}
            icon='rightArrow'
            iconPosition='right'
          />
        </div>
      </div>
    ),
    'product-form': (
      <ProductForm
        preloadedProduct={selectedRevisionProduct}
        clientID={params.clientID || ''}
        collaboratorID={params.collaboratorID || ''}
        revisionServiceID={params.revisionServiceID || ''}
        onChangeCurrentComponent={setCurrentComponent}
      />
    )
  };

  return (
    <>
      <div className='step-2__steps'>
        {
          apiType === 'admin' ? (
            <StepsIndicator
              stepsQuantity={3}
              currentStep={2}
            />
          ) : (
            <StepsIndicator
              stepsQuantity={3}
              currentStep={1}
            />
          )
        }
      </div>
      <div className='step-2__content'>
        <div className='step-2'>
          <Title title={apiType === 'admin' ? t('services.step2Products') : t('services.step1Products')} />
          {component[currentComponent]}
        </div>
      </div>
    </>
  );
};

export default Step2;
